import { ReactNode, useCallback, useRef, useState } from "react"
import { NavigationContainer, LinkingOptions } from "@react-navigation/native"
import { View } from "react-native"
import { MOCK } from "../constants.ts"
import { Text } from "../ui-components/Typography"
import { NavigationContext } from "../context/navigationContext.ts"
import { navigationRef } from "../services/NavigationService.ts"
import { ErrorBoundary } from "../components/ErrorBoundary.tsx"
import PosthogProvider from "../components/PosthogProvider"
import { DeepLinkHandler } from "./DeepLinkHandler"
import { PlaidOAuthReturnHandler } from "./PlaidOAuthReturnHandler"
import { RootStackParamList } from "./types.ts"
import { trackScreen } from "../analytics/trackScreen"
import type { PostHogEventProperties } from "@posthog/core"

type NavigationRootProps = {
  linking: LinkingOptions<RootStackParamList>
  children: (isLoggedIn: boolean) => ReactNode
}

export function NavigationRoot({ linking, children }: NavigationRootProps) {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [deepLink, setDeepLink] = useState<string | null>(null)
  const [deepLinkHandled, setDeepLinkHandledState] = useState<boolean>(false)
  const deepLinkHandledRef = useRef(false)
  const [initLinkHandled, setInitLinkHandled] = useState<boolean>(false)
  const [isNavigationReady, setIsNavigationReady] = useState(false)
  const routeNameRef = useRef<string | undefined>(undefined)
  const setDeepLinkHandled = useCallback((handled: boolean) => {
    deepLinkHandledRef.current = handled
    setDeepLinkHandledState(handled)
  }, [])
  const getDeepLinkHandled = useCallback(() => deepLinkHandledRef.current, [])

  return (
    <NavigationContext
      value={{
        isLoggedIn,
        setIsLoggedIn,
        deepLink,
        setDeepLink,
        deepLinkHandled,
        setDeepLinkHandled,
        getDeepLinkHandled,
        initLinkHandled,
        setInitLinkHandled,
      }}
    >
      <NavigationContainer
        linking={linking}
        documentTitle={{
          formatter: (options, route) => `SynchPay App - ${options?.title ?? route?.name}`,
        }}
        ref={navigationRef}
        onReady={() => {
          const route = navigationRef.getCurrentRoute()
          routeNameRef.current = route?.name
          trackScreen(route?.name, route?.params as PostHogEventProperties | undefined)
          setIsNavigationReady(true)
        }}
        onStateChange={() => {
          const route = navigationRef.getCurrentRoute()
          const previousRouteName = routeNameRef.current
          const currentRouteName = route?.name
          if (previousRouteName !== currentRouteName) {
            trackScreen(currentRouteName, route?.params as PostHogEventProperties | undefined)
          }
          routeNameRef.current = currentRouteName
        }}
      >
        <ErrorBoundary>
          <PosthogProvider>
            <PlaidOAuthReturnHandler isNavigationReady={isNavigationReady} />
            <DeepLinkHandler>
              <>
                {MOCK && (
                  <View
                    style={{
                      position: "absolute",
                      top: 0,
                      width: "100%",
                      height: 20,
                      zIndex: 1000,
                      backgroundColor: "red",
                    }}
                  >
                    <Text color="quaternary">MOCK ENABLED</Text>
                  </View>
                )}
                {children(isLoggedIn)}
              </>
            </DeepLinkHandler>
          </PosthogProvider>
        </ErrorBoundary>
      </NavigationContainer>
    </NavigationContext>
  )
}
